import Events from '@utilities/events'
import UserPreferences from '@utilities/user-preferences'

const VIDEO_HOOK = '[js-hook-video]'
const PLAYER_HOOK = '[js-hook-video-player]'

const VIDEO_AUDIO_HOOK = '[js-hook-video-audio]'
const VIDEO_AUDIO_ON_HOOK = '[js-hook-video-audio-on]'
const VIDEO_AUDIO_OFF_HOOK = '[js-hook-video-audio-off]'
const VIDEO_PLAY_HOOK = '[js-hook-video-play]'
const VIDEO_PAUSE_HOOK = '[js-hook-video-pause]'
const VIDEO_REPLAY_HOOK = '[js-hook-video-replay]'

const VIDEO_READY_CLASS = 'video--is-initialised'
const VIDEO_PLAYING_CLASS = 'video--is-playing'
const VIDEO_PAUSED_CLASS = 'video--is-paused'
const VIDEO_REPLAY_CLASS = 'video--is-ended'
const VIDEO_COOKIE_INVALID_CLASS = 'video--has-invalid-cookie'

/**
 * @typedef {HTMLElement & {
 *  _videoIsInitialised: boolean
 * }} VideoElement
 */

/** @type {VideoElement[]} */
const VIDEOS = Array.from(document.querySelectorAll(VIDEO_HOOK))

class Video {
  constructor() {
    this.videos = []
    this.registeredPlatforms = {}
  }

  registerPlatforms(platforms) {
    if (typeof platforms !== 'object') return
    this.registeredPlatforms = platforms

    this._bindEvent()
  }

  /**
   * Bind generic events
   */
  _bindEvent() {
    Events.$on('video::inview', (_event, element) => {
      if (!element.inviewProperties.isInViewport.vertical && !element.dataset.videoLoop) {
        Events.$trigger(`video[${element.id}]::pause`)
      }

      if (!element._videoIsInitialised && element.inviewProperties.scrolledPastViewport.bottom) {
        this.initVideo(element)
      }
    })

    Events.$on('video::update', (_event, element) => {
      if (!element) {
        this.iterateVideos()
      } else {
        this.initVideo(element)
      }
    })

    Events.$on('video::ready', (_event, data) => {
      data.element.classList.add(VIDEO_READY_CLASS)
      data.element.classList.add(VIDEO_PAUSED_CLASS)
    })

    Events.$on('video::playing', (_event, data) => {
      data.element.classList.remove(VIDEO_REPLAY_CLASS)
      data.element.classList.remove(VIDEO_PAUSED_CLASS)
      data.element.classList.add(VIDEO_PLAYING_CLASS)
    })

    Events.$on('video::paused', (_event, data) => {
      data.element.classList.remove(VIDEO_PLAYING_CLASS)
      data.element.classList.add(VIDEO_PAUSED_CLASS)
    })

    Events.$on('video::ended', (_event, data) => {
      data.element.classList.remove(VIDEO_PLAYING_CLASS)
      data.element.classList.add(VIDEO_REPLAY_CLASS)
    })

    Events.$on('video::bind-player-events', (_event, data) => {
      if (data) bindPlayerEvents(data)
    })

    Events.$on('video::cookie-invalid', (_event, element) => {
      if (element) element.classList.add(VIDEO_COOKIE_INVALID_CLASS)
    })
  }

  /**
   * Iterate over platform types
   */
  iterateVideos() {
    this.videos = this.videos.concat(getVideos(this.registeredPlatforms))

    this.videos.forEach(video => {
      this.initVideo(video)
    })
  }

  /**
   * Init all videos
   * @param {object} video
   */
  initVideo(video) {
    if (video._videoIsInitialised) return

    const PlatformClass = this.registeredPlatforms[video.dataset.videoPlatform]
    const options = constructVideoOptions(video)

    if (Object.keys(options).length) {
      options.element.playerInstance = new PlatformClass(options)
    }

    toggleAudioIcon(options.videoMuted, options.element)
  }
}

/**
 * Get all videos matching the hook
 * @param {object} platforms
 * @returns {Object}
 */
function getVideos(platforms) {
  return VIDEOS.filter(video =>
    Object.prototype.hasOwnProperty.call(platforms, video.dataset.videoPlatform) &&
    !video._videoIsInitialised
      ? video
      : false,
  )
}

function toggleAudioIcon(muted, player) {
  const audioOn = player.querySelector(VIDEO_AUDIO_ON_HOOK)
  const audioOff = player.querySelector(VIDEO_AUDIO_OFF_HOOK)
  if (!audioOn || !audioOff) return

  if (muted) {
      audioOn.style.display = 'none';
      audioOff.style.display = 'block';
  } else {
      audioOn.style.display = 'block';
      audioOff.style.display = 'none';
  }
}

/**
 * Construct the video options object
 * @param {VideoElement} element
 * @returns {Object}
 */
function constructVideoOptions(element) {
  const {
    videoPlatform,
    videoId,
    videoSources,
    videoClosedcaptions,
    videoTime = '0',
    videoInfo = '0',
    videoControls = '0',
    videoMuted = '0',
    videoAutoplay = '0',
    videoAutopause = '1',
    videoLoop = '0',
    videoPlaysinline = '0',
  } = element.dataset

  const instanceId = element.id
  const player = element.querySelector(PLAYER_HOOK)

  if (!videoPlatform || !videoId || element._videoIsInitialised) return {}

  element._videoIsInitialised = true

  return {
    element,
    player,
    instanceId,
    videoPlatform,
    videoId,
    videoInfo,
    videoSources,
    videoClosedcaptions,
    // Boolean options:
    videoTime: parseInt(videoTime, 10),
    videoControls: parseInt(videoControls, 10),
    videoMuted: parseInt(videoMuted, 10),
    videoAutoplay: UserPreferences.prefersReducedMotion ? 0 : parseInt(videoAutoplay, 10),
    videoAutopause: parseInt(videoAutopause, 10),
    videoPlaysinline: parseInt(videoPlaysinline, 10),
    videoLoop: parseInt(videoLoop, 10),
  }
}

/**
 * Bind all the player specific events
 * @param {Object} options
 */
function bindPlayerEvents(options) {
  Events.$on(`video[${options.instanceId}]::play`, () => {
    options.element.playerInstance.play()
  })

  Events.$on(`video[${options.instanceId}]::pause`, () => {
    options.element.playerInstance.pause()
  })

  Events.$on(`video[${options.instanceId}]::replay`, () => {
    options.element.playerInstance.replay()
  })

  Events.$on(`video[${options.instanceId}]::mute`, () => {
    options.element.playerInstance.mute()
  })

  Events.$on(`video[${options.instanceId}]::unmute`, () => {
    options.element.playerInstance.unMute()
  })

  Events.$on(`video[${options.instanceId}]::volume`, (_event, data) => {
    options.element.playerInstance.setVolume(data.data)
  })

  const audioButton = options.element.querySelector(VIDEO_AUDIO_HOOK)
  if (audioButton) {
    audioButton.addEventListener('click', () => {
      if (options.videoMuted) {
          options.videoMuted = 0
          Events.$trigger(`video[${options.instanceId}]::unmute`)
          toggleAudioIcon(options.videoMuted, options.element)
      } else {
          options.videoMuted = 1
          Events.$trigger(`video[${options.instanceId}]::mute`)
          toggleAudioIcon(options.videoMuted, options.element)
      }
    })
  }

  const playButton = options.element.querySelector(VIDEO_PLAY_HOOK)
  if (playButton) {
    playButton.addEventListener('click', () => {
      Events.$trigger(`video[${options.instanceId}]::play`)
    })
  }

  const pauseButton = options.element.querySelector(VIDEO_PAUSE_HOOK)
  if (pauseButton) {
    pauseButton.addEventListener('click', () => {
      Events.$trigger(`video[${options.instanceId}]::pause`)
    })
  }

  const replayButton = options.element.querySelector(VIDEO_REPLAY_HOOK)
  if (replayButton) {
    replayButton.addEventListener('click', () => {
      Events.$trigger(`video[${options.instanceId}]::replay`)
    })
  }
}

export default new Video()
